import type { CanvasConfig } from '@/types/canvas';
import type { CreatePrintContextParams } from '@/types/printContext';
import { createPrintContext, type PrintContext } from '@designdeck/shared';

/**
 * Canvas configuration for the guided entry's placement frame.
 *
 * The seller places inside the print area itself, with no garment behind it:
 * the canvas IS the print area, so the dashed border frames the whole surface.
 *
 * No new rendering path is involved — CanvasEditor already skips the mockup
 * layer when `mockupImageUrl` is empty and adds the print-area border either
 * way, so this is the existing editor with one layer left out. Move, corner
 * scale, rotate and delete come from the controls it installs anyway.
 */

/**
 * Smallest print-area width the frame canvas is drawn at, in canvas pixels.
 *
 * A product position measures its print area in mockup pixels — 228 px wide
 * on the reference shirt. Drawn at that size and stretched to the 280 px frame
 * (twice that on a retina display) the motif and the dashed border came out
 * soft. So the frame draws at a scaled-up context instead: same millimetres,
 * more pixels, and the CSS scale goes down rather than up.
 */
export const GUIDED_FRAME_MIN_WIDTH_PX = 560;

/**
 * Print context the frame draws with: the position's area, scaled up to at
 * least GUIDED_FRAME_MIN_WIDTH_PX. Physical size is unchanged, so millimetres
 * and every relative placement stay what they were; only px/mm grows. A
 * position already wide enough is returned as it is.
 */
export function guidedFrameContext(area: PrintContext): PrintContext {
  const factor = Math.max(1, GUIDED_FRAME_MIN_WIDTH_PX / area.widthPx);
  if (!(area.widthPx > 0) || factor === 1) return area;
  return createPrintContext({
    widthPx: Math.round(area.widthPx * factor),
    heightPx: Math.round(area.heightPx * factor),
    widthMm: area.widthMm,
    heightMm: area.heightMm,
    pxPerMm: area.pxPerMm * factor,
  });
}

/**
 * Seam around the print area, as a share of its width.
 *
 * Fabric paints the selection controls onto the canvas itself, so a control
 * sitting outside the object — the delete and rotate handles above it, the
 * scale handle below — needs canvas to be painted on. On a mockup the garment
 * provides that margin; here the canvas is the print area plus this seam. The
 * seam is transparent: the card's own surface shows through it, so it reads
 * as room around the frame rather than as part of it. Relative, so it is the
 * same width on screen whatever the area's pixel size.
 */
export const GUIDED_FRAME_SEAM = 0.12;

/** The seam in canvas pixels for the context the frame draws with. */
export function guidedFramePadding(printContext: { widthPx: number }): number {
  return Math.round(printContext.widthPx * GUIDED_FRAME_SEAM);
}

/**
 * On-screen diameter of the frame's handles, in CSS pixels.
 *
 * The classic editor draws its handles 32 canvas pixels wide on a garment
 * that is displayed scaled down; the frame shows the bare print area at
 * close to native size, where the same handles crowd a 280 px box.
 */
export const GUIDED_FRAME_CONTROL_SIZE = 22;

/**
 * Display-factor floor for the frame's editor. The editor's default floor
 * (0.5) is exactly what the 280 px frame needs for a 560 px context plus seam;
 * a smaller frame (the Polaris embedding draws it at most 184 px wide) would
 * be held at 0.5 and show only part of the canvas, so the frame allows less.
 */
export const GUIDED_FRAME_MIN_SCALE = 0.2;

export function guidedCanvasConfig(printContext: PrintContext): CanvasConfig {
  const pad = guidedFramePadding(printContext);
  return {
    mockupWidth: printContext.widthPx + 2 * pad,
    mockupHeight: printContext.heightPx + 2 * pad,
    // Centred in a canvas that is the print area plus the seam, the area
    // starts at the seam on every side — the same offsets
    // guidedPrintContextParams hands the store.
    printArea: {
      x: pad,
      y: pad,
      width: printContext.widthPx,
      height: printContext.heightPx,
    },
    // Empty on purpose: this is the "no garment" branch in CanvasEditor.
    mockupImageUrl: '',
    // The frame draws the dashed border itself, in CSS, identical to its
    // still preview — Fabric's stroke scales with the display factor and
    // came out heavier and coarser next to the preset frames.
    printAreaBorder: false,
    // The frame box behind the canvas carries the paper colour for the print
    // area; the canvas stays clear so the seam shows the card underneath.
    backgroundColor: 'transparent',
    heatheredOverlayUrl: null,
    textureOverlayUrl: null,
    shadowOverlayUrl: null,
    cutoutMaskUrl: null,
    isHeathered: false,
  };
}

/**
 * The print context that matches the frame canvas above.
 *
 * A context built from a product position carries the offset of the print area
 * *within the mockup* — 136/86 on the reference shirt. The frame has no mockup:
 * its print area sits at the seam offset inside a canvas of its own. Handing
 * the editor the mockup offsets pushed every object out of the frame by exactly
 * that difference, which is why a freshly opened frame looked empty — offsets
 * and canvas size here must be the ones guidedCanvasConfig draws with.
 *
 * Physical size is carried over unchanged, so millimetres keep coming from the
 * print area the seller is actually designing on.
 */
export function guidedPrintContextParams(
  printContext: PrintContext
): CreatePrintContextParams {
  const pad = guidedFramePadding(printContext);
  return {
    widthPx: printContext.widthPx,
    heightPx: printContext.heightPx,
    widthMm: printContext.widthMm,
    heightMm: printContext.heightMm,
    pxPerMm: printContext.pxPerMm,
    mockupWidthPx: printContext.widthPx + 2 * pad,
    mockupHeightPx: printContext.heightPx + 2 * pad,
    offsetX: pad,
    offsetY: pad,
  };
}
